import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { TrendChart } from "@/components/dashboards/TrendChart";
import { DrillDownTable, type Column } from "@/components/dashboards/DrillDownTable";
import { DashboardFilters, defaultFilters, type DashboardFilterState } from "@/components/dashboards/DashboardFilters";
import { useTrend, useDispatchDrillDown } from "@/lib/dashboards";

export const Route = createFileRoute("/_authenticated/dashboards/dispatch")({
  component: DispatchAnalytics,
  head: () => ({ meta: [{ title: "Dispatch analytics — MissionReady" }] }),
});

type Row = { id: string; task: string; priority: string; status: string; created_at: string; completed_at: string | null };

function DispatchAnalytics() {
  const [filters, setFilters] = useState<DashboardFilterState>(defaultFilters());
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const throughput = useTrend("dispatch", filters);
  const responseTime = useTrend("response_time", filters);
  const drill = useDispatchDrillDown({ ...filters, page, pageSize: 50, search });

  const columns: Column<Row>[] = [
    { key: "task", label: "Task" },
    { key: "priority", label: "Priority" },
    { key: "status", label: "Status" },
    { key: "created_at", label: "Created", render: (r) => new Date(r.created_at).toLocaleString() },
    { key: "completed_at", label: "Completed", render: (r) => r.completed_at ? new Date(r.completed_at).toLocaleString() : "—" },
  ];

  return (
    <div className="space-y-4">
      <DashboardFilters value={filters} onChange={setFilters} />
      <div className="grid gap-3 md:grid-cols-2">
        <TrendChart title="Dispatch throughput" points={throughput.data?.points} loading={throughput.isLoading} color="hsl(var(--success))" />
        <TrendChart title="Avg response (sec)" points={responseTime.data?.points} valueKey="total" loading={responseTime.isLoading} color="hsl(var(--primary))" />
      </div>
      <DrillDownTable
        title="Dispatch assignments"
        columns={columns}
        rows={(drill.data?.rows ?? []) as Row[]}
        total={drill.data?.total ?? 0}
        page={page}
        pageSize={50}
        onPageChange={setPage}
        search={search}
        onSearchChange={setSearch}
        loading={drill.isLoading}
        csvName="dispatch.csv"
      />
    </div>
  );
}
